/**
 * Realtime WebSocket client — Arcadia backend `/realtime` (tips / payments).
 * URL comes from `getRealtimeWebSocketUrl()` (same host as `VITE_API_URL`, or dev proxy).
 */

import { getRealtimeWebSocketUrl } from "@/lib/api-url";

export interface RealtimeEvent {
  event: "INSERT" | "UPDATE" | "DELETE";
  schema: string;
  table: string;
  new?: any;
  old?: any;
}

export interface RealtimeMessage {
  type: "subscribed" | "unsubscribed" | "event" | "error" | "pong";
  channel?: string;
  event?: RealtimeEvent;
  error?: string;
}

export type RealtimeEventHandler = (event: RealtimeEvent) => void;

class RealtimeClient {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000;
  private handlers: Map<string, Set<RealtimeEventHandler>> = new Map();
  private isConnecting = false;
  private shouldReconnect = true;

  constructor(url?: string) {
    this.url = url ?? getRealtimeWebSocketUrl();
  }

  /**
   * Connect to WebSocket server
   */
  connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (this.isConnecting) {
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            clearInterval(checkInterval);
            resolve();
          } else if (!this.isConnecting) {
            clearInterval(checkInterval);
            reject(new Error("Connection failed"));
          }
        }, 100);
      });
    }

    this.isConnecting = true;

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
          console.log("[Realtime] Connected to WebSocket server");
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: RealtimeMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error("[Realtime] Error parsing message:", error);
          }
        };

        this.ws.onerror = (error) => {
          console.error("[Realtime] WebSocket error:", error);
          this.isConnecting = false;
          if (this.reconnectAttempts === 0) {
            reject(error);
          }
        };

        this.ws.onclose = () => {
          console.log("[Realtime] WebSocket connection closed");
          this.isConnecting = false;
          this.ws = null;

          if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
            console.log(
              `[Realtime] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
            );

            setTimeout(() => {
              this.connect().catch(() => {
                /* will retry on next close */
              });
            }, delay);
          }
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  private handleMessage(message: RealtimeMessage): void {
    switch (message.type) {
      case "event":
        if (message.channel && message.event) {
          const handlers = this.handlers.get(message.channel);
          if (handlers) {
            handlers.forEach((handler) => {
              try {
                handler(message.event!);
              } catch (error) {
                console.error(`[Realtime] Error in handler for ${message.channel}:`, error);
              }
            });
          }
        }
        break;

      case "subscribed":
        console.log(`[Realtime] Subscribed to ${message.channel}`);
        break;

      case "unsubscribed":
        console.log(`[Realtime] Unsubscribed from ${message.channel}`);
        break;

      case "error":
        console.error(`[Realtime] Server error: ${message.error}`);
        break;

      case "pong":
        break;
    }
  }

  subscribe(channel: string, handler: RealtimeEventHandler): () => void {
    if (!this.handlers.has(channel)) {
      this.handlers.set(channel, new Set());
    }

    this.handlers.get(channel)!.add(handler);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({
        type: "subscribe",
        channel,
      });
    } else {
      this.connect()
        .then(() => {
          this.send({
            type: "subscribe",
            channel,
          });
        })
        .catch((error) => {
          console.error("[Realtime] Failed to connect for subscription:", error);
        });
    }

    return () => {
      this.unsubscribe(channel, handler);
    };
  }

  unsubscribe(channel: string, handler?: RealtimeEventHandler): void {
    if (handler) {
      const handlers = this.handlers.get(channel);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.handlers.delete(channel);
        }
      }
    } else {
      this.handlers.delete(channel);
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({
        type: "unsubscribe",
        channel,
      });
    }
  }

  private send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  disconnect(): void {
    this.shouldReconnect = false;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.handlers.clear();
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

let clientInstance: RealtimeClient | null = null;

export function getRealtimeClient(url?: string): RealtimeClient {
  if (!clientInstance) {
    clientInstance = new RealtimeClient(url);
  }
  return clientInstance;
}
